/**
 * Contacts Service (Redux Toolkit) - Paginated, multimode contact data (list, by category, on matter).
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API } from "api/axios.config";

//----------------------------------------------------------------------------
// API
//----------------------------------------------------------------------------
const CONTACTS_BASE = "/Contacts";

const getContactsList = (page, limit, parameter, active, categoryId) =>
  API.get(
    `${CONTACTS_BASE}?page=${page}&limit=${limit}&orderby=${parameter}${active !== undefined ? `&active=${active}` : ""}${categoryId ? `&categoryid=${categoryId}` : ""}`,
  );

const getContactsListSearch = (search, page, limit, parameter, active, categoryId) =>
  API.get(
    `${CONTACTS_BASE}?search=${encodeURIComponent(search)}&page=${page}&limit=${limit}&orderby=${parameter}${active !== undefined ? `&active=${active}` : ""}${categoryId ? `&categoryid=${categoryId}` : ""}`,
  );

const getAllContactsforCategory = (categoryId, page, limit) =>
  API.get(
    `${CONTACTS_BASE}?categoryid=${categoryId}&orderby=name&page=${page}&limit=${limit}`
  );

const searchContactsforCategory = (categoryId, search, page, limit) =>
  API.get(
    `${CONTACTS_BASE}?categoryid=${categoryId}&search=${encodeURIComponent(search)}&orderby=name&page=${page}&limit=${limit}`,
  );

const getContactsOnMatter = (matterId) =>
  API.get(`/Matters/${matterId}${CONTACTS_BASE}?active=true`);

//----------------------------------------------------------------------------
// Helpers
//----------------------------------------------------------------------------
const buildOrderBy = (sort) => {
  if (!sort) return "name";
  if (sort.dir === "desc") return `${sort.field} desc`;
  return sort.field;
};

//----------------------------------------------------------------------------
// Async thunks
//----------------------------------------------------------------------------
export const fetchContactsList = createAsyncThunk(
  "contactsList/fetchList",
  async ({ page, limit, sort, searchValue, type, active, categoryId }) => {
    if (page === undefined) return undefined;
    try {
      const parameter = buildOrderBy(sort);
      const res = searchValue
        ? await getContactsListSearch(searchValue, page, limit, parameter, active, categoryId)
        : await getContactsList(page, limit, parameter, active, categoryId);
      return { data: res.data, type };
    } catch (err) {
      throw err;
    }
  }
);

export const fetchContactsForCategory = createAsyncThunk(
  "contactsList/fetchByCategory",
  async ({ categoryId, page, limit, searchValue }) => {
    if (page === undefined) return undefined;
    try {
      const res = searchValue
        ? await searchContactsforCategory(categoryId, searchValue, page, limit)
        : await getAllContactsforCategory(categoryId, page, limit);
      return res.data;
    } catch (err) {
      throw err;
    }
  }
);

export const fetchContactsOnMatter = createAsyncThunk(
  "contactsList/fetchOnMatter",
  async (matterId) => {
    try {
      const res = await getContactsOnMatter(matterId);
      return res.data;
    } catch (err) {
      throw err;
    }
  }
);

//----------------------------------------------------------------------------
// Initial state
//----------------------------------------------------------------------------
const initialState = {
  contactsList: undefined,
  contactsByCategory: undefined,
  allContacts: undefined,
  contactsOnMatter: undefined,
  contactsInfo: {},
  isLoading: undefined,
  IsError: false,
  isSuccess: false,
  contactError: null,
};

//----------------------------------------------------------------------------
// Slice
//----------------------------------------------------------------------------
const contactsSlice = createSlice({
  name: "contactsList",
  initialState,
  reducers: {
    clearContactsList: (state) => {
      state.contactsList = undefined;
    },
    setContactsInfo: (state, action) => {
      state.contactsInfo = action.payload;
    },
    clearContactInfo: (state) => {
      state.contactsInfo = {};
    },
    setContactError: (state, action) => {
      state.contactError = action.payload;
    },
    resetContactError: (state) => {
      state.contactError = null;
    },
    contactCreated: (state) => {
      state.contactError = false;
    },
  },
  extraReducers: (builder) => {
    // fetchContactsList
    builder
      .addCase(fetchContactsList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchContactsList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.IsError = false;
        state.isSuccess = true;
        if (action.payload === undefined) {
          state.contactsList = undefined;
          return;
        }
        const { data, type } = action.payload;
        if (type === "attachContact") {
          state.allContacts = data;
        } else {
          state.contactsList = data;
        }
      })
      .addCase(fetchContactsList.rejected, (state) => {
        state.isLoading = false;
        state.IsError = true;
        state.isSuccess = false;
      });

    // fetchContactsForCategory
    builder
      .addCase(fetchContactsForCategory.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchContactsForCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.contactsByCategory = action.payload;
        state.IsError = false;
        state.isSuccess = true;
      })
      .addCase(fetchContactsForCategory.rejected, (state) => {
        state.isLoading = false;
        state.IsError = true;
      });

    // fetchContactsOnMatter
    builder
      .addCase(fetchContactsOnMatter.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchContactsOnMatter.fulfilled, (state, action) => {
        state.isLoading = false;
        state.contactsOnMatter = action.payload;
        state.IsError = false;
        state.isSuccess = true;
      })
      .addCase(fetchContactsOnMatter.rejected, (state) => {
        state.isLoading = false;
        state.IsError = true;
      });
  },
});

export const {
  clearContactsList,
  setContactsInfo,
  clearContactInfo,
  setContactError,
  resetContactError,
  contactCreated,
} = contactsSlice.actions;

export const contactsReducer = contactsSlice.reducer;
export default contactsReducer;
